import React from 'react';
import { 
  Box, 
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
  Chip
} from '@mui/material';
import { 
  Article as ArticleIcon,
  Lightbulb as LightbulbIcon,
  AccountTree as AccountTreeIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Quiz as QuizIcon,
  Help as HelpIcon,
  ViewAgenda as ViewAgendaIcon,
  MenuBook as MenuBookIcon,
  Translate as TranslateIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { OutputFormat } from './OutputFormatSelector';

// Format info interface
interface FormatInfo {
  id: OutputFormat;
  title: string;
  description: string;
  icon: React.ReactNode;
  isNew?: boolean;
  isPremium?: boolean;
}

// Format info data
const formatInfoData: FormatInfo[] = [
  {
    id: 'fullCapsule',
    title: 'Full Capsule',
    description: 'Complete insights from the video with all important information preserved.',
    icon: <ArticleIcon />
  },
  {
    id: 'keyInsights',
    title: 'Key Insights',
    description: 'Essential takeaways and most important points in a concise format.',
    icon: <LightbulbIcon />
  },
  {
    id: 'mindMap',
    title: 'Mind Map',
    description: 'Visual representation of concepts and their relationships.',
    icon: <AccountTreeIcon />
  },
  {
    id: 'aiChat',
    title: 'AI Chat',
    description: 'Ask questions about the video content and get instant answers.',
    icon: <QuestionAnswerIcon />,
    isNew: true
  },
  {
    id: 'mcqs',
    title: 'MCQs',
    description: 'Test your understanding with multiple-choice questions.',
    icon: <QuizIcon />,
    isPremium: true
  },
  {
    id: 'faqs',
    title: 'FAQs',
    description: 'Common questions and answers about the video content.',
    icon: <HelpIcon />
  },
  {
    id: 'flashCards',
    title: 'Flash Cards',
    description: 'Quick, digestible cards for memorization and review.',
    icon: <ViewAgendaIcon />,
    isPremium: true
  },
  {
    id: 'studyNotes',
    title: 'Study Notes',
    description: 'Detailed notes organized in an academic format.',
    icon: <MenuBookIcon />
  },
  {
    id: 'terminology',
    title: 'Terminology',
    description: 'Simplified explanations of complex terms and concepts.',
    icon: <TranslateIcon />
  }
];

interface OutputFormatSelectorCompactProps {
  selectedFormat: OutputFormat;
  onFormatChange: (format: OutputFormat) => void;
  userSubscription?: 'free' | 'basic' | 'premium';
}

const OutputFormatSelectorCompact: React.FC<OutputFormatSelectorCompactProps> = ({
  selectedFormat,
  onFormatChange,
  userSubscription = 'free'
}) => {
  // Check if format is available for user's subscription
  // For testing purposes, all formats are available
  const isFormatAvailable = (format: FormatInfo): boolean => {
    return true; // Make all formats available for testing
  };
  
  // Handle format selection
  const handleFormatSelect = (format: OutputFormat) => {
    const formatInfo = formatInfoData.find(f => f.id === format);
    if (formatInfo && isFormatAvailable(formatInfo)) {
      onFormatChange(format);
    }
  };
  
  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle1" fontWeight={500} sx={{ px: 2, mb: 1 }}>
        Output Format
      </Typography>
      
      <List sx={{ width: '100%', p: 0 }}>
        {formatInfoData.map((format) => {
          const isAvailable = isFormatAvailable(format);
          const isSelected = selectedFormat === format.id;
          
          const listItem = (
            <ListItem 
              disablePadding 
              key={format.id}
              sx={{ 
                opacity: isAvailable ? 1 : 0.6,
              }}
            >
              <ListItemButton
                selected={isSelected}
                onClick={() => handleFormatSelect(format.id)}
                disabled={!isAvailable}
                sx={{
                  borderRadius: 1,
                  mx: 1,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(0, 180, 216, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 180, 216, 0.2)',
                    },
                  },
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: isSelected ? 'primary.main' : 'text.secondary',
                    minWidth: 40
                  }}
                >
                  {format.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={format.title} 
                  sx={{ 
                    '& .MuiListItemText-primary': {
                      color: isSelected ? 'primary.main' : 'text.primary',
                      fontWeight: isSelected ? 500 : 400,
                    }
                  }}
                />
                
                {format.isNew && (
                  <Chip 
                    label="New" 
                    size="small" 
                    color="secondary" 
                    sx={{ 
                      height: 20, 
                      fontSize: '0.625rem',
                      fontWeight: 'bold',
                      '& .MuiChip-label': { px: 1 }
                    }} 
                  />
                )}
                
                {format.isPremium && !isAvailable && (
                  <LockIcon fontSize="small" sx={{ color: 'text.disabled', ml: 1 }} />
                )}
              </ListItemButton>
            </ListItem>
          );
          
          return !isAvailable ? (
            <Tooltip 
              key={format.id}
              title="Upgrade to Premium to unlock this format" 
              arrow
            >
              <Box>{listItem}</Box>
            </Tooltip>
          ) : (
            listItem
          );
        })}
      </List>
    </Box>
  );
};

export default OutputFormatSelectorCompact;
